import { AxiosError } from 'axios'

/**
 * Every request error the callers see, so a screen can branch on status
 * without importing axios. Ported from `ApiException` in the Flutter app's
 * `lib/services/api/api_client.dart`.
 */
export class ApiError extends Error {
  readonly status?: number
  readonly code?: string
  /**
   * The raw JSON error body, when the server sent one. Some errors carry an
   * extra field a screen needs — `documents` on `page_in_use`, `fields` on
   * `field_in_use` — and keeping the body means each of those does not grow
   * its own property here.
   */
  readonly details?: Record<string, unknown>
  readonly cause?: unknown

  constructor(init: {
    message: string
    status?: number
    code?: string
    details?: Record<string, unknown>
    cause?: unknown
  }) {
    super(init.message)
    this.name = 'ApiError'
    this.status = init.status
    this.code = init.code
    this.details = init.details
    this.cause = init.cause
  }

  get isUnauthorized() {
    return this.status === 401
  }

  get isForbidden() {
    return this.status === 403
  }

  get isNotFound() {
    return this.status === 404
  }

  static from(error: unknown): ApiError {
    if (error instanceof ApiError) return error

    const axiosError = error as AxiosError
    if (axiosError?.isAxiosError) {
      const body = axiosError.response?.data
      const data = (typeof body === 'object' && body !== null ? body : {}) as Record<
        string,
        unknown
      >
      if (axiosError.response) {
        return new ApiError({
          status: axiosError.response.status,
          // The gateway answers `{error, code}`; a few older handlers send
          // `{message}`.
          message:
            (data.error as string) ??
            (data.message as string) ??
            axiosError.message ??
            'Ein Fehler ist aufgetreten',
          code: data.code as string | undefined,
          details: data,
          cause: error,
        })
      }
      if (axiosError.request) {
        return new ApiError({
          message: 'Keine Verbindung zum Server.',
          code: 'network_error',
          cause: error,
        })
      }
    }

    return new ApiError({
      message: (error as Error)?.message ?? 'Unerwarteter Fehler',
      code: 'unknown_error',
      cause: error,
    })
  }
}

/** The server has `LLM_PASS_ENABLED` off, so the model path is unavailable.
 *  Not an error to log — the editor offers the other detector instead. */
export class LlmDetectionDisabled extends Error {
  constructor() {
    super('LLM field detection is disabled on this installation')
    this.name = 'LlmDetectionDisabled'
  }
}
